import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api.js";
import type { CacheStatus, IntelStatus, Manuscript, ModelRef, Project, ProjectSettings, Scene, SceneReviewStatus } from "../lib/types.js";
import { Icon } from "../components/Icon.js";
import { BrandMark } from "../components/BrandMark.js";
import { volumeLabel } from "../lib/format.js";
import { useToast } from "../components/Toast.js";
import { ConfirmDialog } from "../components/ConfirmDialog.js";
import { AccountMenu } from "../auth/AccountMenu.js";
import { ProjectSettingsModal } from "../settings/ProjectSettings.js";
import { ExportModal } from "../export/ExportModal.js";
import { ImportModal } from "../import/ImportModal.js";
import { Modal } from "../components/Modal.js";
import { Sidebar, type SidebarSection } from "./Sidebar.js";
import { Editor, type EditorControls } from "./Editor.js";
import { SceneInspector } from "./SceneInspector.js";
import { AiPanel } from "./AiPanel.js";
import { RevisionHistory } from "./RevisionHistory.js";
import { Resizer } from "./Resizer.js";
import { SectionView } from "../collections/SectionView.js";
import { ReviewScene } from "../review/ReviewScene.js";
import { ReviewBook } from "../review/ReviewBook.js";
import { OverviewPanel } from "./OverviewPanel.js";
import { GlobalSearch } from "../search/GlobalSearch.js";
import { FindReplace } from "../search/FindReplace.js";
import { ModelPickerProvider } from "../ai/ModelPicker.js";

interface Props {
  projectId: string;
  initialSceneId: string | null;
  onGoDashboard: () => void;
  onProjectChanged: () => Promise<void>;
}

interface DeleteRequest {
  kind: "chapter" | "scene" | "part";
  id: string;
  name: string;
}

const UI_KEY = "clovascribe:workspaceUi";

export function Workspace({ projectId, initialSceneId, onGoDashboard, onProjectChanged }: Props) {
  const toast = useToast();
  const [project, setProject] = useState<Project | null>(null);
  const [settings, setSettings] = useState<ProjectSettings | null>(null);
  const [manuscript, setManuscript] = useState<Manuscript | null>(null);
  const [activeSceneId, setActiveSceneId] = useState<string | null>(initialSceneId);
  const [activeScene, setActiveScene] = useState<Scene | null>(null);
  const [section, setSection] = useState<SidebarSection>(initialSceneId ? "manuscript" : "overview");
  const [overviewReload, setOverviewReload] = useState(0);
  const [cacheStatus, setCacheStatus] = useState<CacheStatus | null>(null);
  const [cacheBuilding, setCacheBuilding] = useState(false);
  const [cacheProgress, setCacheProgress] = useState<{ processed: number; remaining: number } | null>(null);
  const [intel, setIntel] = useState<IntelStatus | null>(null);
  const [charsOrganizing, setCharsOrganizing] = useState(false);
  const [audiobookRendering, setAudiobookRendering] = useState(false);
  const [charsVersion, setCharsVersion] = useState(0);

  const [navCollapsed, setNavCollapsed] = useState(false);
  const [aiCollapsed, setAiCollapsed] = useState(false);
  const [focusMode, setFocusMode] = useState(false);
  const [reviewMode, setReviewMode] = useState(false);
  const [reviewView, setReviewView] = useState<"scroll" | "book">("scroll");
  const [reviewStatuses, setReviewStatuses] = useState<Record<string, SceneReviewStatus>>({});
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showFindReplace, setShowFindReplace] = useState(false);
  // Bumped to force the editor to remount with fresh content (e.g. after a
  // project-wide replace rewrites the scene that's currently open).
  const [editorReload, setEditorReload] = useState(0);
  const [showExport, setShowExport] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [sectionFocusId, setSectionFocusId] = useState<string | null>(null);
  const [deleteRequest, setDeleteRequest] = useState<DeleteRequest | null>(null);

  // Focus mode: Esc exits. Auto-leave focus if you switch away from the manuscript.
  useEffect(() => {
    if (!focusMode) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setFocusMode(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [focusMode]);
  useEffect(() => { if (section !== "manuscript") setFocusMode(false); }, [section]);
  // Leave Review Mode when navigating away from the manuscript.
  useEffect(() => { if (section !== "manuscript") setReviewMode(false); }, [section]);

  const [treeWidth, setTreeWidth] = useState(260);
  const [aiWidth, setAiWidth] = useState(360);

  // Bridge between the editor and the AI panel for applying accepted
  // suggestions. The editor populates this; the AI panel calls into it.
  const editorControlsRef = useRef<EditorControls | null>(null);
  const [selectedText, setSelectedText] = useState("");

  // Restore panel widths + collapse preferences (persisted between visits).
  useEffect(() => {
    try {
      const raw = localStorage.getItem(UI_KEY);
      if (raw) {
        const ui = JSON.parse(raw) as { treeWidth?: number; aiWidth?: number; aiCollapsed?: boolean; navCollapsed?: boolean };
        if (ui.treeWidth) setTreeWidth(ui.treeWidth);
        if (ui.aiWidth) setAiWidth(ui.aiWidth);
        if (typeof ui.aiCollapsed === "boolean") setAiCollapsed(ui.aiCollapsed);
        if (typeof ui.navCollapsed === "boolean") setNavCollapsed(ui.navCollapsed);
      }
    } catch {
      /* ignore */
    }
  }, []);
  useEffect(() => {
    try {
      localStorage.setItem(UI_KEY, JSON.stringify({ treeWidth, aiWidth, aiCollapsed, navCollapsed }));
    } catch {
      /* ignore */
    }
  }, [treeWidth, aiWidth, aiCollapsed, navCollapsed]);

  const refreshCache = useCallback(async () => {
    try {
      const r = await api.cacheStatus(projectId);
      setCacheStatus(r.status);
    } catch {
      /* ignore — the dot is a hint, not critical */
    }
  }, [projectId]);

  // The cache build runs here (not inside the Book Cache panel) so it keeps
  // going — and keeps driving the sidebar's spinning indicator — even after you
  // navigate away from that page.
  const runCacheBuild = useCallback(async (model: ModelRef) => {
    setCacheBuilding(true);
    setCacheProgress({ processed: 0, remaining: 0 });
    let total = 0;
    let stalled = false;
    try {
      for (let guard = 0; guard < 500; guard++) {
        const res = await api.buildCache(projectId, model);
        total += res.processed;
        setCacheStatus(res.status);
        setCacheProgress({ processed: total, remaining: res.remaining });
        if (res.remaining === 0) break;
        // A stage produced nothing but work remains — the model returned no usable
        // output (common with reasoning models on a tight budget). Stop, don't loop
        // forever, and tell the user rather than falsely reporting success.
        if (res.processed === 0) { stalled = true; break; }
      }
      if (stalled) toast.error("Couldn't finish building — the AI returned no usable output for some items. Try again or pick a different model.");
      else toast.success(total > 0 ? `Built ${total} item${total === 1 ? "" : "s"}.` : "Cache already up to date.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Caching failed");
    } finally {
      setCacheBuilding(false);
      setCacheProgress(null);
      void refreshCache();
    }
  }, [projectId, refreshCache, toast]);

  // AI character organize runs here so it keeps going (with a sidebar spinner) when
  // you navigate away from the Characters page; the panel reloads on charsVersion.
  const runOrganize = useCallback(async (model: ModelRef) => {
    setCharsOrganizing(true);
    try {
      const r = await api.organizeCharacters(projectId, model);
      toast.success(`Organized ${r.assigned} character${r.assigned === 1 ? "" : "s"} into ${r.sectionsCreated + r.sectionsReused} section${r.sectionsCreated + r.sectionsReused === 1 ? "" : "s"}.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Auto-organize failed");
    } finally {
      setCharsOrganizing(false);
      setCharsVersion((v) => v + 1);
    }
  }, [projectId, toast]);

  const refreshIntel = useCallback(async () => {
    try {
      const r = await api.extractionStatus(projectId);
      setIntel(r.intel);
    } catch {
      /* ignore — the dot is a hint, not critical */
    }
  }, [projectId]);

  const loadManuscript = useCallback(async () => {
    const data = await api.manuscript(projectId);
    setManuscript(data);
    void refreshCache();
    void refreshIntel();
    return data;
  }, [projectId, refreshCache, refreshIntel]);

  // While an auto-populate job is running (including one started from the Insights
  // panel, or already running when the workspace opened), poll so the sidebar's
  // Insights indicator keeps spinning and flips to fresh/stale when it finishes.
  useEffect(() => {
    if (!intel?.running) return;
    const t = setInterval(() => void refreshIntel(), 3000);
    return () => clearInterval(t);
  }, [intel?.running, refreshIntel]);

  // Poll whether an audiobook render is running so the sidebar shows a spinner on
  // the Audiobook item from any page (renders run server-side and keep going when
  // you leave the page). Light endpoint; polled on a slow cadence.
  useEffect(() => {
    let cancelled = false;
    const check = () => api.audiobookRendering(projectId)
      .then((r) => { if (!cancelled) setAudiobookRendering(r.rendering); })
      .catch(() => undefined);
    void check();
    const t = setInterval(check, 5000);
    return () => { cancelled = true; clearInterval(t); };
  }, [projectId]);

  // Re-check freshness whenever you move around the workspace, so the Insights /
  // Book Cache dots reflect prose you've added since (autosave doesn't reload the
  // manuscript, so the dots would otherwise lag until the next reload).
  useEffect(() => {
    void refreshCache();
    void refreshIntel();
  }, [section, activeSceneId, refreshCache, refreshIntel]);

  const loadScene = useCallback(async (sceneId: string) => {
    try {
      const { scene } = await api.getScene(sceneId);
      setActiveScene(scene);
      setActiveSceneId(scene.id);
    } catch {
      setActiveScene(null);
    }
  }, []);

  // Enter Review Mode for the open scene: save pending edits first, refresh the
  // scene so the review view shows the latest saved prose, and load review
  // statuses. Reviewing is read-only and never mutates the manuscript.
  const enterReview = useCallback(async () => {
    if (!activeScene) return;
    try { await editorControlsRef.current?.saveNow(); } catch { /* ignore */ }
    try {
      const { scene } = await api.getScene(activeScene.id);
      setActiveScene(scene);
    } catch { /* ignore */ }
    try {
      const { statuses } = await api.reviewStatuses(projectId);
      setReviewStatuses(Object.fromEntries(statuses.map((s) => [s.sceneId, s.status])));
    } catch { /* ignore */ }
    setReviewMode(true);
  }, [activeScene, projectId]);

  const setSceneReviewStatus = useCallback(async (sceneId: string, status: SceneReviewStatus) => {
    setReviewStatuses((prev) => ({ ...prev, [sceneId]: status }));
    try { await api.setSceneReviewStatus(projectId, sceneId, status); } catch { /* ignore */ }
  }, [projectId]);

  // Initial load.
  useEffect(() => {
    (async () => {
      try {
        const [detail, data] = await Promise.all([api.getProject(projectId), loadManuscript()]);
        setProject(detail.project);
        setSettings(detail.settings);
        const target =
          (initialSceneId && data.scenes.some((s) => s.id === initialSceneId) && initialSceneId) ||
          (detail.project.lastEditedSceneId &&
            data.scenes.some((s) => s.id === detail.project.lastEditedSceneId) &&
            detail.project.lastEditedSceneId) ||
          firstSceneId(data);
        if (target) await loadScene(target);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to open workspace");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const selectScene = useCallback(
    (sceneId: string) => {
      if (sceneId === activeSceneId) {
        setSection("manuscript");
        return;
      }
      setSection("manuscript");
      void loadScene(sceneId);
    },
    [activeSceneId, loadScene]
  );

  const openSectionItem = useCallback((target: SidebarSection, focusId: string) => {
    setSection(target);
    setSectionFocusId(focusId);
  }, []);

  // Global search (Ctrl/Cmd+K) and find & replace (Ctrl/Cmd+Shift+H) shortcuts.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setShowSearch(true);
      } else if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === "h") {
        e.preventDefault();
        setShowFindReplace(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Update cached counts after a save so the tree and totals stay live.
  const applySavedCounts = useCallback((sceneId: string, wordCount: number, charCount: number) => {
    setManuscript((prev) => {
      if (!prev) return prev;
      const scenes = prev.scenes.map((s) =>
        s.id === sceneId ? { ...s, wordCount, charCount } : s
      );
      const scene = scenes.find((s) => s.id === sceneId);
      const chapters = prev.chapters.map((c) =>
        scene && c.id === scene.chapterId
          ? { ...c, wordCount: scenes.filter((s) => s.chapterId === c.id).reduce((sum, s) => sum + s.wordCount, 0) }
          : c
      );
      const wordTotal = scenes.reduce((sum, s) => sum + s.wordCount, 0);
      return { ...prev, scenes, chapters, totals: { ...prev.totals, wordCount: wordTotal } };
    });
  }, []);

  const onSceneTitleChanged = useCallback((sceneId: string, title: string) => {
    setManuscript((prev) =>
      prev ? { ...prev, scenes: prev.scenes.map((s) => (s.id === sceneId ? { ...s, title } : s)) } : prev
    );
    setActiveScene((prev) => (prev && prev.id === sceneId ? { ...prev, title } : prev));
  }, []);

  const confirmDelete = useCallback(async (toVault: boolean) => {
    if (!deleteRequest) return;
    const { kind, id } = deleteRequest;
    try {
      if (kind === "scene") await api.deleteScene2(id, toVault);
      else if (kind === "chapter") await api.deleteChapter2(id, toVault);
      else await api.deletePart(id);
      const data = await loadManuscript();
      // If we deleted the open scene (or its chapter), pick another.
      if (kind !== "part" && !data.scenes.some((s) => s.id === activeSceneId)) {
        const next = firstSceneId(data);
        if (next) await loadScene(next);
        else {
          setActiveScene(null);
          setActiveSceneId(null);
        }
      }
      const label = `${kind[0]!.toUpperCase()}${kind.slice(1)}`;
      toast.success(toVault && kind !== "part" ? `${label} moved to Vault.` : `${label} deleted.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Delete failed");
    } finally {
      setDeleteRequest(null);
    }
  }, [deleteRequest, activeSceneId, loadManuscript, loadScene, toast]);

  // Chapter title + scene position for the editor header eyebrow and status bar.
  const sceneMeta = useMemo(() => {
    if (!manuscript || !activeScene) return { chapterTitle: "", sceneIndex: 0, sceneTotal: 0 };
    const chapter = manuscript.chapters.find((c) => c.id === activeScene.chapterId);
    const inChapter = manuscript.scenes
      .filter((s) => s.chapterId === activeScene.chapterId)
      .sort((a, b) => a.position - b.position);
    return {
      chapterTitle: chapter?.title ?? "",
      sceneIndex: inChapter.findIndex((s) => s.id === activeScene.id) + 1,
      sceneTotal: inChapter.length
    };
  }, [manuscript, activeScene]);

  // Live base counts for the editor (everything except the active scene).
  const { chapterBaseWords, projectBaseWords } = useMemo(() => {
    if (!manuscript || !activeScene) return { chapterBaseWords: 0, projectBaseWords: 0 };
    const activeSummary = manuscript.scenes.find((s) => s.id === activeScene.id);
    const activeSaved = activeSummary?.wordCount ?? 0;
    const chapterTotal = manuscript.scenes
      .filter((s) => s.chapterId === activeScene.chapterId)
      .reduce((sum, s) => sum + s.wordCount, 0);
    return {
      chapterBaseWords: chapterTotal - activeSaved,
      projectBaseWords: manuscript.totals.wordCount - activeSaved
    };
  }, [manuscript, activeScene]);

  if (!project || !manuscript || !settings) {
    return (
      <div className="auth-screen">
        <Icon name="book" size={26} className="spin" style={{ color: "var(--accent)" }} />
      </div>
    );
  }

  const deleteMessage =
    deleteRequest?.kind === "chapter"
      ? `Delete “${deleteRequest.name}” and all of its scenes? This cannot be undone.`
      : deleteRequest?.kind === "part"
        ? `Delete the part “${deleteRequest?.name}”? Its chapters will move out of the part but are kept.`
        : `Delete the scene “${deleteRequest?.name}”? This cannot be undone.`;

  return (
    <ModelPickerProvider projectId={project.id}>
    <div className={`workspace${focusMode ? " workspace--focus" : ""}`} style={{ ["--tree-w" as string]: `${treeWidth}px`, ["--ai-w" as string]: `${aiWidth}px` }}>
      {focusMode && (
        <button className="focus-exit" onClick={() => setFocusMode(false)} title="Exit focus mode (Esc)">
          <Icon name="x" size={15} /> Exit focus
        </button>
      )}
      <header className="topbar glass">
        <div className="row gap-2" style={{ minWidth: 0 }}>
          <BrandMark size={30} />
          <span className="strong truncate" style={{ maxWidth: 260 }}>{project.title}</span>
          {volumeLabel(project) && <span className="topbar-volume truncate">{volumeLabel(project)}</span>}
        </div>
        <div className="spacer" />
        <button className="btn btn-sm btn-ghost topbar-search" onClick={() => setShowSearch(true)} title="Search (Ctrl/Cmd+K)">
          <Icon name="search" size={15} /> Search <kbd className="search-kbd">⌘K</kbd>
        </button>
        <button className="btn btn-sm btn-ghost" onClick={() => setShowFindReplace(true)} title="Find & Replace across the manuscript (Ctrl/Cmd+Shift+H)">
          <Icon name="edit" size={15} /> Replace
        </button>
        <button className="btn btn-sm btn-ghost" onClick={() => setShowExport(true)}>
          <Icon name="file" size={15} /> Export
        </button>
        <button
          className="btn btn-sm btn-primary"
          onClick={() => window.open(`/read/${projectId}`, "_blank", "noopener")}
          title="Open the manuscript in a book reader (new tab)"
        >
          <Icon name="book" size={15} /> Open E-Book
        </button>
        <button className="btn btn-sm btn-ghost" onClick={() => setShowSettings(true)}>
          <Icon name="settings" size={15} /> Settings
        </button>
        <AccountMenu />
      </header>

      <div className="workspace-body">
        <div className="panel-sidebar">
          <Sidebar
            project={project}
            manuscript={manuscript}
            section={section}
            onSectionChange={(s) => { setSection(s); setSectionFocusId(null); }}
            activeSceneId={activeSceneId}
            projectId={projectId}
            onSelectScene={selectScene}
            onManuscriptChanged={async () => { await loadManuscript(); }}
            onRequestDelete={(kind, id, name) => setDeleteRequest({ kind, id, name })}
            onGoDashboard={onGoDashboard}
            navCollapsed={navCollapsed}
            onToggleNav={() => setNavCollapsed((c) => !c)}
            cacheState={cacheStatus?.state ?? null}
            cacheBuilding={cacheBuilding}
            insightsState={intel?.state ?? null}
            insightsSyncing={intel?.running ?? false}
            charsOrganizing={charsOrganizing}
            audiobookRendering={audiobookRendering}
          />
        </div>
        {section === "manuscript" && (
          <Resizer side="left" width={treeWidth} min={200} max={420} onResize={setTreeWidth} />
        )}

        <main className="panel-editor">
          {/* The editor stays mounted whenever a scene is open; it is only
              hidden (never unmounted) when another section is active, so its
              live content is never lost on a section round-trip. */}
          {section === "manuscript" && reviewMode && activeScene && reviewView === "scroll" && (
            <ReviewScene
              key={`review:${activeScene.id}`}
              projectId={projectId}
              scene={activeScene}
              chapterTitle={sceneMeta.chapterTitle}
              reviewStatus={reviewStatuses[activeScene.id] ?? "unread"}
              onStatusChange={(s) => void setSceneReviewStatus(activeScene.id, s)}
              onExit={() => setReviewMode(false)}
              onApplied={(s) => { setActiveScene(s); applySavedCounts(s.id, s.wordCount, s.charCount); }}
              view={reviewView}
              onToggleView={() => setReviewView("book")}
            />
          )}
          {section === "manuscript" && reviewMode && activeScene && reviewView === "book" && (
            <ReviewBook
              key={`reviewbook:${activeScene.chapterId}`}
              projectId={projectId}
              chapterId={activeScene.chapterId}
              chapterTitle={sceneMeta.chapterTitle}
              reviewStatus={reviewStatuses[activeScene.id] ?? "unread"}
              onStatusChange={(s) => void setSceneReviewStatus(activeScene.id, s)}
              onExit={() => setReviewMode(false)}
              onApplied={(s) => { setActiveScene(s); applySavedCounts(s.id, s.wordCount, s.charCount); }}
              onToggleView={() => setReviewView("scroll")}
            />
          )}
          {activeScene && (
            <div className="editor-host" style={{ display: section === "manuscript" && !reviewMode ? "flex" : "none" }}>
              <Editor
                key={`${activeScene.id}:${editorReload}`}
                scene={activeScene}
                autosaveIntervalMs={settings.autosaveIntervalMs}
                chapterBaseWords={chapterBaseWords}
                projectBaseWords={projectBaseWords}
                chapterTitle={sceneMeta.chapterTitle}
                sceneIndex={sceneMeta.sceneIndex}
                sceneTotal={sceneMeta.sceneTotal}
                onSaved={applySavedCounts}
                onTitleChange={onSceneTitleChanged}
                onOpenHistory={() => setShowHistory(true)}
                onToggleInspector={() => setInspectorOpen((o) => !o)}
                onToggleFocus={() => setFocusMode(true)}
                onToggleAi={() => setAiCollapsed((c) => !c)}
                onEnterReview={() => void enterReview()}
                aiCollapsed={aiCollapsed}
                controlsRef={editorControlsRef}
                onSelectionChange={setSelectedText}
              />
              {inspectorOpen && (
                <SceneInspector
                  scene={activeScene}
                  onClose={() => setInspectorOpen(false)}
                  onUpdated={(s) => setActiveScene(s)}
                />
              )}
            </div>
          )}
          {section === "manuscript" && !activeScene && (
            <div className="editor-empty">
              <Icon name="book" size={40} style={{ color: "var(--text-faint)" }} />
              <h2>No scene selected</h2>
              <p className="muted">Choose a scene from the navigator, or create one to start writing.</p>
            </div>
          )}
          {section === "overview" && (
            <OverviewPanel
              projectId={projectId}
              reloadKey={overviewReload}
              onOpenScene={selectScene}
              onOpenManuscript={() => setSection("manuscript")}
              onOpenSection={(s) => { setSection(s); setSectionFocusId(null); }}
              onExport={() => setShowExport(true)}
              onSettings={() => setShowSettings(true)}
              onProjectChanged={() => { setOverviewReload((n) => n + 1); void onProjectChanged(); }}
            />
          )}
          {section !== "manuscript" && section !== "overview" && (
            <SectionView
              projectId={projectId}
              section={section}
              manuscript={manuscript}
              onOpenScene={selectScene}
              onOpenSection={(s) => { setSection(s); setSectionFocusId(null); }}
              onManuscriptChanged={async () => { await loadManuscript(); }}
              onCacheChanged={refreshCache}
              onIntelChanged={refreshIntel}
              cacheStatus={cacheStatus}
              cacheBuilding={cacheBuilding}
              cacheProgress={cacheProgress}
              onBuildCache={runCacheBuild}
              charsOrganizing={charsOrganizing}
              charsVersion={charsVersion}
              onOrganizeCharacters={runOrganize}
              focusId={sectionFocusId}
            />
          )}
        </main>

        {section === "manuscript" && !reviewMode && !aiCollapsed && (
          <>
            <Resizer side="right" width={aiWidth} min={300} max={520} onResize={setAiWidth} />
            <div className="panel-ai">
              <AiPanel
                projectId={projectId}
                scene={activeScene}
                selectedText={selectedText}
                controlsRef={editorControlsRef}
                onCollapse={() => setAiCollapsed(true)}
                onOpenScene={selectScene}
              />
            </div>
          </>
        )}
        {section === "manuscript" && !reviewMode && aiCollapsed && !focusMode && (
          <button className="ai-edge" title="Open Scribe AI" onClick={() => setAiCollapsed(false)}>
            <Icon name="sparkles" size={16} />
            <span className="ai-edge-label">Scribe AI</span>
          </button>
        )}
      </div>

      {showHistory && activeScene && (
        <RevisionHistory
          scene={activeScene}
          onClose={() => setShowHistory(false)}
          onRestored={(s) => {
            setActiveScene(s);
            applySavedCounts(s.id, s.wordCount, s.charCount);
          }}
        />
      )}
      {showSettings && (
        <ProjectSettingsModal
          project={project}
          settings={settings}
          onClose={() => setShowSettings(false)}
          onSaved={(p) => {
            setProject(p);
            setOverviewReload((n) => n + 1);
            void onProjectChanged();
            api.getProject(projectId).then((d) => setSettings(d.settings)).catch(() => undefined);
          }}
          onDeleted={() => { setShowSettings(false); void onProjectChanged(); onGoDashboard(); }}
        />
      )}
      {deleteRequest?.kind === "part" && (
        <ConfirmDialog
          title="Delete part"
          message={deleteMessage}
          confirmLabel="Delete"
          danger
          onConfirm={() => void confirmDelete(false)}
          onCancel={() => setDeleteRequest(null)}
        />
      )}
      {deleteRequest && deleteRequest.kind !== "part" && (
        <Modal
          title={`Delete ${deleteRequest.kind}`}
          subtitle={deleteRequest.name}
          width={480}
          onClose={() => setDeleteRequest(null)}
          footer={
            <>
              <button className="btn btn-ghost" onClick={() => setDeleteRequest(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={() => void confirmDelete(false)}>
                <Icon name="trash" size={15} /> Delete permanently
              </button>
              <button className="btn btn-primary" onClick={() => void confirmDelete(true)}>
                <Icon name="vault" size={15} /> Move to Vault
              </button>
            </>
          }
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            {deleteRequest.kind === "chapter"
              ? "Move this chapter and all its scenes to the Vault, where you can restore it later — or delete it permanently."
              : "Move this scene to the Vault, where you can restore it later — or delete it permanently."}
          </p>
        </Modal>
      )}

      {showExport && (
        <ExportModal
          projectId={projectId}
          onClose={() => setShowExport(false)}
          onImport={() => {
            setShowExport(false);
            setShowImport(true);
          }}
        />
      )}
      {showImport && (
        <ImportModal
          projectId={projectId}
          onClose={() => setShowImport(false)}
          onApplied={async () => {
            await loadManuscript();
            if (activeSceneId) await loadScene(activeSceneId);
            setOverviewReload((n) => n + 1);
          }}
        />
      )}
      {showSearch && (
        <GlobalSearch
          projectId={projectId}
          onClose={() => setShowSearch(false)}
          onOpenScene={selectScene}
          onOpenSection={openSectionItem}
        />
      )}
      {showFindReplace && (
        <FindReplace
          projectId={projectId}
          onClose={() => setShowFindReplace(false)}
          onReplaced={async () => {
            await loadManuscript();
            if (activeSceneId) await loadScene(activeSceneId);
            // Remount the editor so the open scene shows the replaced text.
            setEditorReload((n) => n + 1);
          }}
        />
      )}
    </div>
    </ModelPickerProvider>
  );
}

function firstSceneId(m: Manuscript): string | null {
  const chapterOrder = new Map(m.chapters.map((c) => [c.id, c.position]));
  const sorted = [...m.scenes].sort((a, b) => {
    const ca = chapterOrder.get(a.chapterId) ?? 0;
    const cb = chapterOrder.get(b.chapterId) ?? 0;
    return ca - cb || a.position - b.position;
  });
  return sorted[0]?.id ?? null;
}
